import React, { createContext, useContext, useReducer, useCallback } from 'react'

/**
 * Unified Notification Context for Ovacs
 *
 * This context provides a centralized state management for all notifications
 * across the application. It works in conjunction with useOvacsNotifications hook
 * for real-time SSE streaming.
 *
 * Features:
 * - Unified notification state
 * - Unread count tracking
 * - Local state updates (optimistic UI)
 * - Support for all notification types
 * - Workspace-aware
 */

// Initial state
const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  lastFetched: null,
  stats: null,
}

// Action types
const NOTIFICATION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  MARK_AS_READ: 'MARK_AS_READ',
  MARK_AS_UNREAD: 'MARK_AS_UNREAD',
  MARK_MULTIPLE_AS_READ: 'MARK_MULTIPLE_AS_READ',
  MARK_MULTIPLE_AS_UNREAD: 'MARK_MULTIPLE_AS_UNREAD',
  MARK_ALL_AS_READ: 'MARK_ALL_AS_READ',
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',
  SET_STATS: 'SET_STATS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  RESET: 'RESET',
}

// Notification reducer
const notificationReducer = (state, action) => {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.unread_count || state.unreadCount,
        isLoading: false,
        error: null,
        lastFetched: new Date().toISOString(),
      }

    case NOTIFICATION_ACTIONS.ADD_NOTIFICATION: {
      const newNotification = action.payload

      // Check if notification already exists (prevent duplicates)
      const exists = state.notifications.some(n => n.id === newNotification.id)
      if (exists) {
        return state
      }

      // Add to beginning of array
      const updatedNotifications = [newNotification, ...state.notifications]

      // Update unread count if notification is unread
      const newUnreadCount = newNotification.is_read
        ? state.unreadCount
        : state.unreadCount + 1

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION: {
      const updatedNotification = action.payload
      const updatedNotifications = state.notifications.map(n =>
        n.id === updatedNotification.id ? { ...n, ...updatedNotification } : n
      )

      return {
        ...state,
        notifications: updatedNotifications,
      }
    }

    case NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION: {
      const notificationId = action.payload
      const notification = state.notifications.find(n => n.id === notificationId)
      const updatedNotifications = state.notifications.filter(n => n.id !== notificationId)

      // Decrement unread count if removed notification was unread
      const newUnreadCount = notification && !notification.is_read
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.MARK_AS_READ: {
      const notificationId = action.payload
      const updatedNotifications = state.notifications.map(n =>
        n.id === notificationId
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n
      )

      // Find if notification was unread before
      const wasUnread = state.notifications.find(n => n.id === notificationId && !n.is_read)
      const newUnreadCount = wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.MARK_AS_UNREAD: {
      const notificationId = action.payload
      const updatedNotifications = state.notifications.map(n =>
        n.id === notificationId
          ? { ...n, is_read: false, read_at: null }
          : n
      )

      // Find if notification was read before
      const wasRead = state.notifications.find(n => n.id === notificationId && n.is_read)
      const newUnreadCount = wasRead ? state.unreadCount + 1 : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_READ: {
      const notificationIds = action.payload
      const updatedNotifications = state.notifications.map(n =>
        notificationIds.includes(n.id)
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n
      )

      // Count how many were actually unread
      const unreadMarked = state.notifications.filter(
        n => notificationIds.includes(n.id) && !n.is_read
      ).length
      const newUnreadCount = Math.max(0, state.unreadCount - unreadMarked)

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_UNREAD: {
      const notificationIds = action.payload
      const updatedNotifications = state.notifications.map(n =>
        notificationIds.includes(n.id)
          ? { ...n, is_read: false, read_at: null }
          : n
      )

      // Count how many were actually read
      const readMarked = state.notifications.filter(
        n => notificationIds.includes(n.id) && n.is_read
      ).length
      const newUnreadCount = state.unreadCount + readMarked

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      }
    }

    case NOTIFICATION_ACTIONS.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(n => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
        unreadCount: 0,
      }

    case NOTIFICATION_ACTIONS.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload,
      }

    case NOTIFICATION_ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload,
      }

    case NOTIFICATION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }

    case NOTIFICATION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }

    case NOTIFICATION_ACTIONS.RESET:
      return initialState

    default:
      return state
  }
}

// Create context
const OvacsNotificationContext = createContext(null)

// Provider component
export const OvacsNotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState)

  // Action creators
  const actions = {
    setLoading: useCallback((isLoading) => {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: isLoading })
    }, []),

    setNotifications: useCallback((notifications, unread_count) => {
      dispatch({
        type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS,
        payload: { notifications, unread_count },
      })
    }, []),

    addNotification: useCallback((notification) => {
      dispatch({ type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION, payload: notification })
    }, []),

    updateNotification: useCallback((notification) => {
      dispatch({ type: NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION, payload: notification })
    }, []),

    removeNotification: useCallback((notificationId) => {
      dispatch({ type: NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION, payload: notificationId })
    }, []),

    markAsRead: useCallback((notificationId) => {
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_AS_READ, payload: notificationId })
    }, []),

    markAsUnread: useCallback((notificationId) => {
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_AS_UNREAD, payload: notificationId })
    }, []),

    markMultipleAsRead: useCallback((notificationIds) => {
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_READ, payload: notificationIds })
    }, []),

    markMultipleAsUnread: useCallback((notificationIds) => {
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_UNREAD, payload: notificationIds })
    }, []),

    markAllAsRead: useCallback(() => {
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_ALL_AS_READ })
    }, []),

    setUnreadCount: useCallback((count) => {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_UNREAD_COUNT, payload: count })
    }, []),

    setStats: useCallback((stats) => {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_STATS, payload: stats })
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error })
    }, []),

    clearError: useCallback(() => {
      dispatch({ type: NOTIFICATION_ACTIONS.CLEAR_ERROR })
    }, []),

    reset: useCallback(() => {
      dispatch({ type: NOTIFICATION_ACTIONS.RESET })
    }, []),
  }

  // Computed values
  const computed = {
    // Get unread notifications
    getUnreadNotifications: useCallback(() => {
      return state.notifications.filter(n => !n.is_read)
    }, [state.notifications]),

    // Get notifications by type
    getNotificationsByType: useCallback((type) => {
      return state.notifications.filter(n => n.notification_type === type)
    }, [state.notifications]),

    // Get notifications by priority
    getNotificationsByPriority: useCallback((priority) => {
      return state.notifications.filter(n => n.priority === priority)
    }, [state.notifications]),

    // Get urgent/high priority unread notifications
    getUrgentNotifications: useCallback(() => {
      return state.notifications.filter(
        n => !n.is_read && (n.priority === 'urgent' || n.priority === 'high')
      )
    }, [state.notifications]),
  }

  const value = {
    // State
    ...state,
    // Actions
    ...actions,
    // Computed
    ...computed,
  }

  return (
    <OvacsNotificationContext.Provider value={value}>
      {children}
    </OvacsNotificationContext.Provider>
  )
}

// Custom hook to use notification context
export const useOvacsNotificationContext = () => {
  const context = useContext(OvacsNotificationContext)
  if (!context) {
    throw new Error(
      'useOvacsNotificationContext must be used within OvacsNotificationProvider'
    )
  }
  return context
}

export default OvacsNotificationContext
